// ============================================================================
// CrewAhead Maritime Platform - Availability Post API Functions
// ============================================================================

import { apiClient, handleApiError } from './client';
import { API_ENDPOINTS } from '../utils/constants';
import type {
  AvailabilityPost,
  AvailabilityPostFilters,
  AvailabilityPostStatus,
  PaginatedResponse,
} from '../types';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CreateAvailabilityPostData {
  postType: string;
  rankId?: number | null;
  shorePositionId?: number | null;
  preferredVesselTypeIds?: number[];
  headline?: string | null;
  description?: string | null;
  rankOther?: string;
  shorePositionOther?: string;
  willingToJoinAnyVessel?: boolean;
  contractDuration?: string | null;
  employmentTypes?: string[];
  availableFrom?: string | null;
  availableImmediately?: boolean;
  currentLocation?: string | null;
  willingToRelocate?: boolean;
  preferredLocations?: string[];
  expectedSalaryMin?: number | null;
  expectedSalaryMax?: number | null;
  salaryNegotiable?: boolean;
  salaryCurrency?: string;
  tags?: string[];
  expiresAt?: string | null;
  // Status is set via separate publish/pause actions; included here for
  // direct status updates (e.g., pausing an active post).
  status?: AvailabilityPostStatus;
}

export type UpdateAvailabilityPostData = Partial<CreateAvailabilityPostData>;

// ─── Availability Post API ───────────────────────────────────────────────────

/**
 * Lists all published availability posts with optional filtering.
 *
 * @param filters - Filter criteria (rank, vessel type, experience, etc.)
 * @returns Paginated list of availability posts
 *
 * @example
 * ```ts
 * const { results, count } = await listAvailabilityPosts({
 *   postType: 'onboard',
 *   rank: 'chief_officer',
 *   status: 'active',
 *   sortBy: 'newest',
 * });
 * ```
 */
export async function listAvailabilityPosts(
  filters: AvailabilityPostFilters = {}
): Promise<PaginatedResponse<AvailabilityPost>> {
  try {
    const params = new URLSearchParams();

    // Apply filters
    if (filters.postType) params.set('post_type', filters.postType);
    if (filters.rank) params.set('rank', filters.rank);
    if (filters.vesselType) params.set('vessel_type', filters.vesselType);
    if (filters.nationality) params.set('nationality', filters.nationality);
    if (filters.status) params.set('status', filters.status);
    if (filters.preferredRegion) params.set('preferred_region', filters.preferredRegion);
    if (filters.contractDuration) params.set('contract_duration', filters.contractDuration);
    if (filters.willingToRelocate !== undefined)
      params.set('willing_to_relocate', String(filters.willingToRelocate));
    if (filters.salaryMin !== undefined) params.set('salary_min', String(filters.salaryMin));
    if (filters.salaryMax !== undefined) params.set('salary_max', String(filters.salaryMax));
    if (filters.salaryCurrency) params.set('salary_currency', filters.salaryCurrency);
    if (filters.preferredContact) params.set('preferred_contact', filters.preferredContact);

    // Sorting
    if (filters.sortBy) params.set('sort_by', filters.sortBy);

    // Pagination
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));

    const queryString = params.toString();
    const url = `${API_ENDPOINTS.AVAILABILITY.LIST}${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<PaginatedResponse<AvailabilityPost>>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Fetches a single availability post by ID.
 *
 * @param id - The availability post ID
 * @returns The availability post details
 *
 * @example
 * ```ts
 * const post = await getAvailabilityPost('123');
 *
 * ```
 */
export async function getAvailabilityPost(id: string): Promise<AvailabilityPost> {
  try {
    const response = await apiClient.get<AvailabilityPost>(
      API_ENDPOINTS.AVAILABILITY.DETAIL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Creates a new availability post (initially as a draft).
 *
 * @param data - Availability post data
 * @returns The created availability post
 *
 * @example
 * ```ts
 * const post = await createAvailabilityPost({
 *   postType: 'onboard',
 *   rankId: 5,
 *   preferredVesselTypeIds: [2, 7],
 *   headline: 'Experienced Chief Officer seeking new opportunity',
 *   contractDuration: '4 months',
 *   availableFrom: '2026-03-01',
 *   expectedSalaryMin: 8000,
 *   expectedSalaryMax: 12000,
 *   salaryCurrency: 'USD',
 * });
 * ```
 */
export async function createAvailabilityPost(
  data: CreateAvailabilityPostData
): Promise<AvailabilityPost> {
  try {
    const response = await apiClient.post<AvailabilityPost>(
      API_ENDPOINTS.AVAILABILITY.CREATE,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Updates an existing availability post.
 *
 * @param id - The availability post ID
 * @param data - Partial data to update
 * @returns The updated availability post
 *
 * @example
 * ```ts
 * const updated = await updateAvailabilityPost('123', {
 *   availableImmediately: true,
 *   expectedSalaryMin: 9000,
 *   preferredLocations: ['Singapore', 'Dubai'],
 * });
 * ```
 */
export async function updateAvailabilityPost(
  id: string,
  data: UpdateAvailabilityPostData
): Promise<AvailabilityPost> {
  try {
    const response = await apiClient.patch<AvailabilityPost>(
      API_ENDPOINTS.AVAILABILITY.UPDATE(id),
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Deletes an availability post.
 *
 * @param id - The availability post ID to delete
 *
 * @example
 * ```ts
 * await deleteAvailabilityPost('123');
 * ```
 */
export async function deleteAvailabilityPost(id: string): Promise<void> {
  try {
    await apiClient.delete<void>(API_ENDPOINTS.AVAILABILITY.DELETE(id));
  } catch (error) {
    throw handleApiError(error);
  }
}

export interface ContactAvailabilityPostData {
  message?: string;
  contactMethod?: 'platform' | 'email' | 'phone' | 'whatsapp';
}

/**
 * Contacts a candidate via their availability post.
 *
 * @param id - The availability post ID
 * @param data - Contact message and method
 * @returns The created contact log
 */
export async function contactAvailabilityPost(
  id: string,
  data: ContactAvailabilityPostData = {}
): Promise<unknown> {
  try {
    const response = await apiClient.post<unknown>(
      API_ENDPOINTS.AVAILABILITY.CONTACT(id),
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Publishes a draft availability post, making it visible to employers.
 *
 * @param id - The availability post ID to publish
 * @returns The published availability post
 *
 * @example
 * ```ts
 * const published = await publishAvailabilityPost('123');
 * console.log(published.status); // 'published'
 * ```
 */
export async function publishAvailabilityPost(id: string): Promise<AvailabilityPost> {
  try {
    const response = await apiClient.post<AvailabilityPost>(
      API_ENDPOINTS.AVAILABILITY.PUBLISH(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Fetches the current user's own availability posts.
 *
 * @returns Array of the user's availability posts
 *
 * @example
 * ```ts
 * const { results } = await getMyAvailabilityPosts();
 * // Includes both drafts and published posts
 * ```
 */
export async function getMyAvailabilityPosts(): Promise<PaginatedResponse<AvailabilityPost>> {
  try {
    const response = await apiClient.get<PaginatedResponse<AvailabilityPost>>(
      API_ENDPOINTS.AVAILABILITY.MY_POSTS
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Fetches the available filter options for availability posts.
 * Returns unique values for ranks, vessel types, nationalities, regions,
 * languages, and skills that can be used as filter dropdowns.
 *
 * @returns Filter options object
 *
 * @example
 * ```ts
 * const filters = await getAvailabilityFilterOptions();
 * // { ranks: [...], vesselTypes: [...], regions: [...], ... }
 * ```
 */
export async function getAvailabilityFilterOptions(): Promise<{
  ranks: string[];
  vesselTypes: string[];
  nationalities: string[];
  regions: string[];
  languages: string[];
  skills: string[];
  contractDurations: string[];
}> {
  try {
    const response = await apiClient.get<{
      ranks: string[];
      vesselTypes: string[];
      nationalities: string[];
      regions: string[];
      languages: string[];
      skills: string[];
      contractDurations: string[];
    }>(API_ENDPOINTS.AVAILABILITY.FILTERS);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}
